import { sortBy, each, camelCase, cloneDeep, uniq, keyBy, values, findIndex, reduce } from 'lodash';
import CountryQuery from 'country-query';
import moment from 'moment';
import {
  ADD_OPPORTUNITIES, FETCH_OPEN_STAGES, ADD_ANALYSIS_OPPORTUNITY_LOGS,
} from './AnalysisActions';
import { DASHBOARD_FETCH_WIN_LOSS_OPPORTUNITY_REQUESTED_FEEDBACK } from '../Dashboard/DashboardActions';

// Initial State
const initialState = {
  opportunities: [],
  geoStats: {},
  status: null,
  errors: null,
  overview: {},
  openStages: {
    data: [],
    status: null,
    errors: null,
  },
};

function makeOverviewStats(opportunities) {
  const bucket = {
    total: 0, amount: 0, min: null, max: 0, stats: { won: { total: 0, amount: 0 }, lost: { total: 0, amount: 0 }, noDecision: { total: 0, amount: 0 } },
  };
  each(opportunities, (item) => {
    bucket.total += 1;
    bucket.amount += item.amount;
    bucket.max = Math.max(bucket.max, item.amount);
    const stage = camelCase(item.stage);
    if (bucket.min === null) {
      bucket.min = item.amount;
    } else {
      bucket.min = Math.min(bucket.min, item.amount);
    }
    if (bucket.stats[stage]) {
      bucket.stats[stage].total += 1;
      bucket.stats[stage].amount += item.amount;
    }
  });
  const wonAmount = bucket.stats.won ? bucket.stats.won.amount : 0;
  const wonAmountRate = bucket.amount ? (wonAmount / bucket.amount) * 100 : 0;
  const lostAmount = bucket.stats.lost ? bucket.stats.lost.amount : 0;
  const lostAmountRate = bucket.amount ? (lostAmount / bucket.amount) * 100 : 0;
  const noDecisionAmount = bucket.stats.noDecision ? bucket.stats.noDecision.amount : 0;
  const noDecisionAmountRate = bucket.amount ? (noDecisionAmount / bucket.amount) * 100 : 0;

  const wonCount = bucket.stats.won ? bucket.stats.won.total : 0;
  const wonCountRate = bucket.total ? (wonCount / bucket.total) * 100 : 0;
  const lostCount = bucket.stats.lost ? bucket.stats.lost.total : 0;
  const lostCountRate = bucket.total ? (lostCount / bucket.total) * 100 : 0;
  const noDecisionCount = bucket.stats.noDecision ? bucket.stats.noDecision.total : 0;
  const noDecisionCountRate = bucket.total ? (noDecisionCount / bucket.total) * 100 : 0;

  return {
    count: bucket.total,
    amount: bucket.amount,
    average: bucket.total !== 0 ? bucket.amount / bucket.total : 0,
    min: bucket.min,
    max: bucket.max,
    wonAmount,
    wonAmountRate,
    lostAmount,
    lostAmountRate,
    noDecisionAmount,
    noDecisionAmountRate,
    wonCount,
    wonCountRate,
    wonAverage: wonCount !== 0 ? wonAmount / wonCount : 0,
    lostCount,
    lostCountRate,
    lostAverage: lostCount !== 0 ? lostAmount / lostCount : 0,
    noDecisionCount,
    noDecisionCountRate,
    noDecisionAverage: noDecisionCount !== 0 ? noDecisionAmount / noDecisionCount : 0,
  };
}

function makeGeoStat(items, worldStats) {
  const stages = ['won', 'lost', 'noDecision'];

  each(items, (item) => {
    let itemTotalAmount = 0;
    let itemTotalCount = 0;
    each(stages, (stage) => {
      itemTotalAmount += item.amount[stage];
      itemTotalCount += item.count[stage];
    });

    item.activeReps = uniq(item.reps).length;

    each(stages, (stage) => {
      item.average[stage] = item.count[stage] !== 0 ? item.amount[stage] / item.count[stage] : 0;
      item.amountRate[stage] = itemTotalAmount !== 0 ? item.amount[stage] / itemTotalAmount : 0;
      item.countRate[stage] = itemTotalCount !== 0 ? item.count[stage] / itemTotalCount : 0;
      const worldTotalAmount = worldStats.amount[stage];
      const worldTotalCount = worldStats.count[stage];

      item.amountRateWorld[stage] = worldTotalAmount !== 0 ? item.amount[stage] / worldTotalAmount : 0;
      item.countRateWorld[stage] = worldTotalCount !== 0 ? item.count[stage] / worldTotalCount : 0;

      if (item.items) {
        makeGeoStat(item.items, worldStats);
      }
    });
  });

  return items;
}

function makeGeoStats(opportunities) {
  const materRegionCodes = {
    Europe: 'emea',
    Africa: 'emea',
    'Northern America': 'am',
    'South America': 'am',
    Asia: 'asoc',
    Oceania: 'asoc',
  };
  const regionCodes = {
    Europe: 'eu',
    Africa: 'af',
    'Northern America': 'na',
    'South America': 'sa',
    Asia: 'as',
    Oceania: 'oc',
  };
  const hashedQuery = {};
  const stats = {};
  const oneStat = {
    count: { won: 0, lost: 0, noDecision: 0 },
    amount: { won: 0, lost: 0, noDecision: 0 },
    amountRate: { won: 0, lost: 0, noDecision: 0 },
    countRate: { won: 0, lost: 0, noDecision: 0 },
    amountRateWorld: { won: 0, lost: 0, noDecision: 0 }, // relative to world
    countRateWorld: { won: 0, lost: 0, noDecision: 0 }, // relative to world
    average: { won: 0, lost: 0, noDecision: 0 },
    activeReps: 0,
    reps: [],
  };
  const worldStats = cloneDeep(oneStat);
  const otherStats = cloneDeep(oneStat);
  each(opportunities, (opportunity) => {
    // determine master region, region, country
    // find the primary contact
    const countryString = opportunity.country_code;
    let country = null;
    if (countryString) {
      if (!hashedQuery[countryString]) {
        hashedQuery[countryString] = CountryQuery.findByAltSpelling(countryString) ||
          CountryQuery.findByNameCommon(countryString) ||
          CountryQuery.findByNameNative(countryString) ||
          CountryQuery.findByNameOfficial(countryString);
      }
      country = hashedQuery[countryString];
    }
    opportunity.country_code = country ? country.cca2.toLowerCase() : null;
    opportunity.region = country ? country.region : null;
    opportunity.subregion = country ? country.subregion : null;
    if (opportunity.region === 'Americas') {
      if (opportunity.subregion !== 'Northern America') {
        // ok, if we are in Americas, but it's not 'Northern America',
        // then group them into 'South America',
        // because there are many different subregions (like Caribean), not just 'South America'
        opportunity.subregion = 'South America';
      }
      opportunity.region = opportunity.subregion;
    }
    opportunity.master_region_code = materRegionCodes[opportunity.region];
    opportunity.region_code = regionCodes[opportunity.region];
    const masterRegionCode = opportunity.master_region_code;
    const regionCode = opportunity.region_code;
    const countryCode = opportunity.country_code;

    const opportunityAmount = opportunity.amount || 0;
    const opportunityOwner = opportunity.owner;
    const opportunityStage = camelCase(opportunity.stage); // Won, Lost, No Decision
    let found = false;
    if (masterRegionCode) {
      if (!stats[masterRegionCode]) {
        stats[masterRegionCode] = {
          ...cloneDeep(oneStat),
          items: {},
        };
      }
      if (regionCode) {
        if (!stats[masterRegionCode].items[regionCode]) {
          stats[masterRegionCode].items[regionCode] = {
            ...cloneDeep(oneStat),
            items: {},
          };
        }
        if (countryCode) {
          if (!stats[masterRegionCode].items[regionCode].items[countryCode]) {
            stats[masterRegionCode].items[regionCode].items[countryCode] = cloneDeep(oneStat);
          }

          stats[masterRegionCode].count[opportunityStage] += 1;
          stats[masterRegionCode].amount[opportunityStage] += opportunityAmount;
          stats[masterRegionCode].items[regionCode].count[opportunityStage] += 1;
          stats[masterRegionCode].items[regionCode].amount[opportunityStage] += opportunityAmount;
          stats[masterRegionCode].items[regionCode].items[countryCode].count[opportunityStage] += 1;
          stats[masterRegionCode].items[regionCode].items[countryCode].amount[opportunityStage] += opportunityAmount;

          if (opportunityOwner && opportunityOwner.is_active) {
            const ownerId = opportunityOwner.user_id;
            stats[masterRegionCode].reps.push(ownerId);
            stats[masterRegionCode].items[regionCode].reps.push(ownerId);
            stats[masterRegionCode].items[regionCode].items[countryCode].reps.push(ownerId);
          }
          found = true;
        }
      }
    }

    if (!found) {
      otherStats.count[opportunityStage] += 1;
      otherStats.amount[opportunityStage] += opportunityAmount;
      if (opportunityOwner && opportunityOwner.is_active) {
        otherStats.reps.push(opportunity.opportunity_id);
      }
    }

    worldStats.count[opportunityStage] += 1;
    worldStats.amount[opportunityStage] += opportunityAmount;
  });
  stats.others = otherStats;

  makeGeoStat(stats, worldStats);

  return stats;
}

const AnalysisReducer = (state = initialState, action) => {
  switch (action.type) {
    case ADD_OPPORTUNITIES: {
      const opportunities = sortBy(action.opportunities, 'amount');
      const indexedOwners = action.owners || {};
      const indexedAccounts = action.accounts || {};
      const indexedProducts = action.products || {};
      each(opportunities, (opportunity) => {
        opportunity.owner = indexedOwners[opportunity.owner_id];
        opportunity.account = indexedAccounts[opportunity.account_id];
        const closeDate = opportunity.close_date ? moment(opportunity.close_date, 'YYYY-MM-DD') : null;
        const createdDate = opportunity.created_date ? moment(opportunity.created_date, 'YYYY-MM-DD') : null;
        opportunity.close_date = closeDate;
        opportunity.created_date = createdDate;
        opportunity.products = reduce(values(keyBy(opportunity.line_items, 'product_id')), (products, lineItem) => {
          if (indexedProducts[lineItem.product_id]) {
            products.push(indexedProducts[lineItem.product_id].name);
          }
          return products;
        }, []);
      });
      const overview = makeOverviewStats(opportunities);
      const geoStats = makeGeoStats(opportunities);
      return {
        ...state, geoStats, opportunities, overview, status: action.status, errors: action.errors,
      };
    }
    case FETCH_OPEN_STAGES: {
      const { stages, status, errors } = action;
      return {
        ...state, openStages: { data: stages, status, errors },
      };
    }
    case ADD_ANALYSIS_OPPORTUNITY_LOGS: {
      const { opportunities } = state;
      const { status, logDetails, opportunityId } = action;
      const idx = findIndex(opportunities, { opportunity_id: opportunityId });
      if (idx >= 0) {
        const newCollection = opportunities;
        newCollection[idx] = { ...newCollection[idx], $status: status, $logDetails: logDetails };
        return { ...state, opportunities: newCollection };
      }
      return state;
    }
    case DASHBOARD_FETCH_WIN_LOSS_OPPORTUNITY_REQUESTED_FEEDBACK: {
      const { total, id } = action;
      const opportunities = reduce(state.opportunities, (items, opportunity) => {
        if (opportunity.opportunity_id === id) {
          items.push({
            ...opportunity,
            requested_feedbacks: total,
          });
        } else {
          items.push(opportunity);
        }
        return items;
      }, []);
      return {
        ...state,
        opportunities,
      };
    }
    default:
      return state;
  }
};

/* Selectors */

// Get all posts
export const getOpportunities = state => state.analysis.opportunities;

export const getOverview = state => state.analysis.overview;
export const getGeoStats = state => state.analysis.geoStats;
export const getLoading = state => state.analysis.status === 'loading';
export const getLoaded = state => state.analysis.status === 'loaded';
export const getHasErrors = state => state.analysis.status === 'error';

export const getOpenStages = state => state.analysis.openStages.data;
export const getOpenStageLoading = state => state.analysis.openStages.status === 'loading';
export const getOpenStageLoaded = state => state.analysis.openStages.status === 'loaded';

export const getErrors = (state) => {
  if (state.analysis.status === 'error') {
    return state.analysis.errors;
  }
  return null;
};

// Export Reducer
export default AnalysisReducer;
